import { Carousel } from './carousel';

export class CarouselGallery extends Carousel {
  /**
   * css selector of carousel container
   * @static
   */
  static selector = '.js-carousel-gallery';

  /**
   * Checks if container node is present in the dom
   * @function isOnPage
   * @returns {Boolean}
   * @static
   */
  static isOnPage() {
    return document.querySelector(CarouselGallery.selector);
  }

  constructor() {
    super();
    this.carousel = new Map();
    super.init(this, CarouselGallery.selector);
    this.announcer = $('.js-carousel-gallery-announcer');
  }

  /**
   * Updates the announcer text with the alt attribute of the currently active slide's image.
   * If the active slide or its image does not have an alt attribute, the announcer text is cleared.
   *
   * @param {Object} swiperInstance - The Swiper instance containing the slides and active index.
   * @param {Array} swiperInstance.slides - Array of slide elements in the Swiper instance.
   * @param {number} swiperInstance.activeIndex - The index of the currently active slide.
   */
  updateAnnouncerText(swiperInstance) {
    if (swiperInstance && swiperInstance.slides && typeof swiperInstance.activeIndex !== 'undefined') {
      const $activeSlide = $(swiperInstance.slides[swiperInstance.activeIndex]);

      if ($activeSlide.length) {
        const $img = $activeSlide.find('img').first();

        if ($img.length && $img.attr('alt')) {
          this.announcer.text($img.attr('alt'));
        } else {
          this.announcer.text('');
        }
      }
    }
  };

  /**
   * Adds event listener for focus to activate the slide.
   * @param {Swiper} swiper - Swiper instance.
   * @param {Node} $carouselElement - DOM element of the carousel.
   */
  enableFocusSlideActivation(swiper, $carouselElement) {
    $carouselElement.addEventListener('focusin', (event) => {
      const slideElement = event.target.closest('.swiper-slide');

      if (slideElement && swiper && $carouselElement.contains(slideElement)) {
        const slideIndex = Array.from(swiper.slides).indexOf(slideElement);

        if (slideIndex > -1 && swiper.activeIndex !== slideIndex) {
          swiper.slideTo(slideIndex, 0);
        }
      }
    });
  }

  /**
   * Generate config depending of the number of the slides in carousel by extending the default config
   * @param {Node} $carousel - Carousel DOM Node
   * @returns {Object} extended config
   */
  generateConfig($carousel) {
    if (super.hasSingleSlide($carousel)) {
      return super.extendConfig({
        pagination: false,
        loop: false,
        autoplay: false
      });
    }

    // eslint-disable-next-line consistent-this
    const self = this;

    return super.extendConfig({
      loop: false,
      slidesPerView: 'auto',
      spaceBetween: 10,
      watchSlidesVisibility: true,
      centeredSlides: true,
      breakpoints: {
        768: {
          centeredSlides: false,
          slidesPerView: 2,
          spaceBetween: 20
        }
      },
      autoplay: super.toggleAutoplay($carousel),
      pagination: {
        el: $carousel.querySelector('.js-carousel-gallery-pagination'),
        clickable: true,
        type: 'bullets',
        renderBullet: (index, className) => {
          const controlsNav = $carousel.querySelector('.carousel-controls');
          const baseLabel = controlsNav?.getAttribute('data-aria-label') || 'Slide';

          return `<button class="${className}" data-index="${index + 1}" aria-label="${baseLabel} ${index + 1}"></button>`;
        }
      },
      navigation: {
        nextEl: $carousel.querySelector('.swiper-button-next'),
        prevEl: $carousel.querySelector('.swiper-button-prev')
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
      },
      on: {
        init: (swiper) =>{
          const paginationElement = swiper.pagination?.el;
          const controlsNav = swiper.el?.querySelector('.carousel-controls');
          const baseLabel = controlsNav?.getAttribute('data-aria-label') || 'Slide';

          if (paginationElement) {
            // eslint-disable-next-line no-unused-expressions
            paginationElement.querySelector('.swiper-pagination-bullet-active')?.setAttribute('aria-current', 'true');
            paginationElement.querySelectorAll('.swiper-pagination-bullet').forEach(bullet => {
              const bulletIndex = bullet.getAttribute('data-index') || (Array.from(bullet.parentNode.children).indexOf(bullet) + 1);
              if (!bullet.hasAttribute('aria-label') || bullet.getAttribute('aria-label') === '') {
                 bullet.setAttribute('aria-label', `${baseLabel} ${bulletIndex}`);
              }
            });
          }

          self.enableFocusSlideActivation(swiper, $carousel);

          const prevBtn = swiper.navigation?.prevEl;
          const nextBtn = swiper.navigation?.nextEl;

          if (prevBtn && !prevBtn.hasAttribute('aria-label')) {
            prevBtn.setAttribute('aria-label', 'Previous Slide');
          }

          if (nextBtn && !nextBtn.hasAttribute('aria-label')) {
            nextBtn.setAttribute('aria-label', 'Next Slide');
          }

          const handleNavKeyPress = (event, action) => {
            if (event.key === 'Enter' || event.key === ' ' || event.keyCode === 13 || event.keyCode === 32) {
              event.preventDefault();
              if (action === 'next' && swiper) {
                swiper.slideNext();
              } else if (action === 'prev' && swiper) {
                swiper.slidePrev();
              }
            }
          };

          if (prevBtn) {
            prevBtn.addEventListener('keydown', (event) => {
              handleNavKeyPress(event, 'prev');
            });
          }

          if (nextBtn) {
            nextBtn.addEventListener('keydown', (event) => {
              handleNavKeyPress(event, 'next');
            });
          }

          swiper.slides.forEach(slide => {
            slide.addEventListener('focusin', () => {
              this.updateAnnouncerText(swiper);
            });
          });
        },

        slideChange: function (swiper) {
          const paginationElement = swiper.pagination?.el;

          if (paginationElement) {
            paginationElement.querySelectorAll('.swiper-pagination-bullet').forEach(bullet => {
              bullet.setAttribute('aria-current', bullet.classList.contains('swiper-pagination-bullet-active') ? 'true' : 'false');
            });
          }
        }
      }
    });
  }
}